import { Recurrence } from '@tableau/self-service-schedules';
import { SelfServiceSchedulePane, SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { SiteUrls } from '@tableau/shared-urls';
import { ButtonType, CompactInlineAlertWidget, DialogFooterButtonsWidget, DropdownButtonType, InlineAlertType, InlineAlertWidget, List, RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption, RadioLayoutType, SingleSelectDropdown } from '@tableau/widgets';
import React from 'react';
import { useState } from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';

import { Messages } from '../../../codegen/Localize';
import { IRemoteRefreshAgentOption } from '../../../datasource/services/ChangeDatasourceRemoteRefreshPropertiesHelper';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { cssClass, Style } from '../../../style';
import { RemoteRefreshTypes } from '../../serverApi/requests/remoteExtractRefresh/GetRemoteRefreshSchedulesRequest';
import { localizeRefreshTargetType } from '../remoteExtractRefresh/EditRemoteRefreshScheduleAction';

export interface BridgeExtractScheduleDialogProps {
    recurrence: Recurrence;
    scheduleOnClientPrompt: string;
    remoteAgentOptions: IRemoteRefreshAgentOption[];
    disableIncremental: boolean;
    showExistingTasksWillBeDeleteWarning: boolean;
    close: () => void;
    scheduleExtract: (recurrence: Recurrence, remoteAgentName: string, refreshType: string) => void;
}

export interface BridgeExtractScheduleDialogState {
    recurrence: Recurrence;
    selectedAgentName: string;
    refreshTypeSelectedIndex: number;
}

export function BridgeExtractScheduleDialogBody(props: BridgeExtractScheduleDialogProps) {
    const initialDropdownText = props.remoteAgentOptions.length === 0 ? Messages.remoteRefreshModeEditRemoteSelectNone() : Messages.remoteRefreshModeEditRemoteSelectEmpty();
    const refreshTypeOptions = Object.values(RemoteRefreshTypes);
    const [recurrence, setRecurrence] = useState(props.recurrence);
    const [selectedAgentName, setSelectedAgentName] = useState(initialDropdownText);
    const [refreshTypeSelectedIndex, setRefreshTypeSelectedIndex] = useState(0);

    const refreshTypeItems: RadioButtonWithLabelOption<string>[] =
        refreshTypeOptions.map((type: string) => ({
            value: type,
            testId: `radio-buttom-${type}`,
            label: localizeRefreshTargetType(type)
        }));

    const server = getSessionManagementServiceInstance();
    const userAccountSettingsLink = new SiteUrls({
        siteUrlName: server.sessionSiteUrlName(),
        queryString: window.location.search
    }).userSettingsUrl(server.sessionUserInfo().username, server.sessionUserInfo().domainName);
    const timeZoneDetails = {
        timeZonesEnabled: true,
        timeZoneDisplayName: server.userTimeZone().displayName,
        linkToUserSettings: userAccountSettingsLink
    };

    const headerStyle: Style = {
        fontWeight: 'normal',
        fontSize: 20,
        paddingBottom: 4
    };
    const headerCSS = cssClass('bridge-extract', 'header', headerStyle);
    return <div>
        {FeatureFlags.bridgeLegacySchedulesRetirementMessageEnabled && <>
            <CompactInlineAlertWidget
                children={Messages.bridgeLegacySchedulesRetirementMessage()}
                type={InlineAlertType.Info}
                testId='bridge-legacy-schedules-retirement-alert'
                style={{ marginBottom: 24 }}
            />
        </>}
        <h1 className={headerCSS}>{Messages.extractTasksDialogTitle_ScheduleLegacyBridgeRefresh()}</h1>
        <span>{props.scheduleOnClientPrompt}</span>
        <div style={{ marginTop: 8, marginBottom: 16 }}>
            <span>{Messages.remoteRefreshAgentSelectV2()}</span>
            <SingleSelectDropdown
                testId={'bridge-extract-dropdown'}
                type={DropdownButtonType.Outline}
                list={<List items={props.remoteAgentOptions.map(agent => ({
                    content: agent.display,
                    value: agent.name,
                    testId: `bridge-extract-dropdown-${agent.name}`
                }))} />}
                disabled={props.remoteAgentOptions.length === 0}
                value={selectedAgentName}
                onChange={(agentName: string) => setSelectedAgentName(agentName)}
                style={{ width: 200, marginTop: 5 }}
                label={''}
            />
        </div>
        <div style={{ marginBottom: 16 }}>
            <span>{Messages.remoteRefreshSelectSchedule()}</span>
            <div style={{ marginTop: 4 }}>
                <SelfServiceSchedulePane
                    recurrenceModel={recurrence}
                    recurrenceModelChangedCallback={(recurrence) => setRecurrence(recurrence)}
                    timeZoneDetails={timeZoneDetails}
                    type={SelfServiceSchedulePaneType.BridgeExtractRefresh}
                    uncollapsible={false}
                    onTimeZoneClick={props.close}
                />
            </div>
        </div>
        <div style={{ marginBottom: 16 }}>
            <span>{Messages.remoteRefreshAgentSelectV2Note()}</span>
        </div>
        {!props.disableIncremental && <>
            <div style={{ marginTop: 16, marginBottom: 16 }}>
                <RadioButtonWithLabelGroupWidget
                    label={Messages.createRemoteRefreshScheduleDialogTypePrompt()}
                    ariaLabel='The type of extract to schedule'
                    buttonList={refreshTypeItems}
                    checkedIndex={refreshTypeSelectedIndex}
                    handleChange={(_: string, checkedIndex: number) => {
                        setRefreshTypeSelectedIndex(checkedIndex);
                    }}
                    layout={RadioLayoutType.Veritical}
                />
            </div>
        </>}
        {props.showExistingTasksWillBeDeleteWarning && <>
            <InlineAlertWidget
                children={Messages.onlineSchedulesDeletedWarning()}
                type={InlineAlertType.Warning}
                testId='bridge-online-schedules-delete-alert'
                style={{ marginTop: 16, marginBottom: 16 }}
            />
        </>}
        <div style={{ paddingTop: 6 }}>
            <DialogFooterButtonsWidget
                buttons={[
                    {
                        handleClick: () => props.close(),
                        buttonType: ButtonType.Outline,
                        children: Messages.cancel(),
                        testId: 'bridge-footer-cancel'
                    },
                    {
                        handleClick: () => props.scheduleExtract(
                            recurrence,
                            selectedAgentName,
                            refreshTypeOptions[refreshTypeSelectedIndex]
                        ),
                        buttonType: ButtonType.Primary,
                        children: Messages.extractTaskCreateLegacyBridgeScheduleConfirm(),
                        disabled: selectedAgentName === initialDropdownText,
                        testId: 'bridge-footer-confirm',
                    }
                ]}
                testId="bridge-extract-dialog-footer"
            />
        </div>
    </div>;
}
